// Right-rail panel listing every sticky tagged as an action item. Click
// an item to select + center its sticky on canvas. Toggle the tag (mark/
// unmark) from a per-row checkbox. Empty state explains the convention.
//
// Visible whenever retro mode is on; the `actions` phase highlights it via
// the PhaseBar but the panel itself is a toggle (Cmd+J or a header button)
// — facilitators often capture action items during the discuss phase too.

import type * as Y from 'yjs';
import { CheckSquare2, Square } from 'lucide-react';
import type { Shape, Sticky } from '@frame/shared';
import { isActionItem, toggleActionItem } from '../actionItems';

interface Props {
  doc: Y.Doc;
  shapes: Shape[];
  onSelectShape: (id: string) => void;
}

export function ActionItemsSidebar({ doc, shapes, onSelectShape }: Props) {
  const items = shapes.filter(isActionItem);

  return (
    <aside
      aria-label="Action items"
      className="
        absolute right-0 top-0 bottom-0 w-[280px] z-10
        bg-bg-panel border-l border-border-default
        flex flex-col
      "
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <h2 className="text-13 font-semibold text-fg-primary">Action items</h2>
        <span className="text-12 text-fg-tertiary tabular-nums">{items.length}</span>
      </header>

      {items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-12 text-fg-tertiary text-center leading-[1.5]">
            No action items yet.<br />
            Tag a sticky as an action to capture it here.
          </p>
        </div>
      ) : (
        <ul className="flex-1 overflow-y-auto py-2">
          {items.map((shape) => (
            <ActionRow
              key={shape.id}
              doc={doc}
              shape={shape}
              onSelect={() => onSelectShape(shape.id)}
            />
          ))}
        </ul>
      )}
    </aside>
  );
}

function ActionRow({
  doc,
  shape,
  onSelect,
}: {
  doc: Y.Doc;
  shape: Sticky;
  onSelect: () => void;
}) {
  const text = shape.text?.trim() || '(empty)';
  return (
    <li className="px-2">
      <div className="flex items-start gap-2 px-2 py-2 rounded-2 hover:bg-bg-hover transition-colors duration-micro">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleActionItem(doc, shape.id);
          }}
          title="Mark complete"
          aria-label="Mark complete"
          className="
            mt-0.5 text-accent
            focus-visible:outline-none focus-visible:shadow-focus rounded-2
          "
        >
          <CheckSquare2 size={16} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={onSelect}
          className="
            flex-1 text-left text-13 text-fg-primary leading-[1.4]
            focus-visible:outline-none focus-visible:shadow-focus rounded-2
          "
        >
          {text}
        </button>
      </div>
    </li>
  );
}

// "Mark as action" button intended for the TextFormatToolbar or context
// menu. Exported so call sites can mount it where they prefer; the
// sidebar uses CheckSquare2 because the row already shows a sticky-as-row.
export function MarkActionButton({
  doc,
  shape,
}: {
  doc: Y.Doc;
  shape: Sticky;
}) {
  const tagged = isActionItem(shape);
  return (
    <button
      type="button"
      onClick={() => toggleActionItem(doc, shape.id)}
      aria-pressed={tagged}
      title={tagged ? 'Unmark as action item' : 'Mark as action item'}
      className={[
        'h-7 px-2.5 inline-flex items-center gap-1.5 rounded-2',
        'text-12 font-medium',
        'transition-colors duration-micro ease-out',
        'focus-visible:outline-none focus-visible:shadow-focus',
        tagged
          ? 'bg-accent text-accent-fg hover:bg-accent-hover'
          : 'text-fg-secondary hover:bg-bg-hover hover:text-fg-primary',
      ].join(' ')}
    >
      {tagged ? <CheckSquare2 size={13} strokeWidth={1.75} /> : <Square size={13} strokeWidth={1.75} />}
      Action
    </button>
  );
}
